/**
 * Evidence collected at the desk when someone is registered on the spot: photos of the CI (and the
 * delegation letter for delegates), the signature (required on Digital-signature events; the tablet is handed to the person)
 * and the acceptance of the participation declaration.
 */

import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { DocumentUploadField } from '../../public/components/DocumentUploadField';
import { SignaturePad } from '../../public/components/SignaturePad';
import { DEFAULT_DECLARATION } from '../../public/types';
import { missingEvidence, requiredEvidence, type DeskEvidence, type DeskKind } from '../deskLogic';

interface DeskEvidenceFieldsProps {
  eventId: string;
  kind: DeskKind;
  value: DeskEvidence;
  onChange: (next: DeskEvidence) => void;
  declarationText?: string | null;
  /** The event signs digitally: the signature pad is mandatory */
  signatureRequired?: boolean;
  disabled?: boolean;
}

export function DeskEvidenceFields({ eventId, kind, value, onChange, declarationText, signatureRequired = false, disabled }: DeskEvidenceFieldsProps) {
  const [showDeclaration, setShowDeclaration] = useState(false);
  const required = requiredEvidence(kind);
  const missing = missingEvidence(kind, value, signatureRequired);

  return (
    <div className="space-y-5">
      <div className="space-y-4">
        <p className="text-sm font-semibold">Fotos</p>
        {required.includes('ci_front') && (
          <DocumentUploadField
            eventId={eventId}
            kind="ci_front"
            label="Cédula de identidad — anverso"
            value={value.ci_front}
            onChange={(key) => onChange({ ...value, ci_front: key })}
            disabled={disabled}
          />
        )}
        {required.includes('ci_back') && (
          <DocumentUploadField
            eventId={eventId}
            kind="ci_back"
            label="Cédula de identidad — reverso"
            value={value.ci_back}
            onChange={(key) => onChange({ ...value, ci_back: key })}
            disabled={disabled}
          />
        )}
        {required.includes('delegation_letter') && (
          <DocumentUploadField
            eventId={eventId}
            kind="delegation_letter"
            label="Carta de delegación"
            hint="Foto de la carta firmada por quien envía al delegado."
            value={value.delegation_letter}
            onChange={(key) => onChange({ ...value, delegation_letter: key })}
            disabled={disabled}
          />
        )}
      </div>

      <div className="space-y-2">
        <p className="text-sm font-semibold">
          Firma{' '}
          <span className="text-xs font-normal text-muted-foreground">
            ({signatureRequired ? 'obligatoria' : 'opcional'}: pase la tablet a la persona)
          </span>
        </p>
        <SignaturePad onChange={(png) => onChange({ ...value, signature: png })} disabled={disabled} />
      </div>

      <div className="space-y-2 rounded-md border p-3">
        <label className="flex items-start gap-3 text-sm">
          <Checkbox
            className="mt-0.5"
            checked={value.declaration}
            onCheckedChange={(c) => onChange({ ...value, declaration: c === true })}
            disabled={disabled}
          />
          <span>
            La persona acepta la declaración de participación y confirma que sus datos son verídicos.
          </span>
        </label>
        <button
          type="button"
          className="flex items-center gap-1 text-xs text-muted-foreground underline underline-offset-2"
          onClick={() => setShowDeclaration((s) => !s)}
        >
          <ChevronDown className={`size-3 transition-transform ${showDeclaration ? 'rotate-180' : ''}`} />
          {showDeclaration ? 'Ocultar declaración' : 'Leer declaración'}
        </button>
        {showDeclaration && <p className="text-xs italic text-muted-foreground">{declarationText || DEFAULT_DECLARATION}</p>}
      </div>

      {missing.length > 0 && <Label className="block text-xs font-normal text-muted-foreground">Falta: {missing.join(', ')}.</Label>}
    </div>
  );
}
